import React, { useState } from 'react';
import MyTable from '../../../../components/MyTable';
import FilterPopup from '../../../../components/FilterPopup';
import { formatPhone } from '../../../../utlis';

const SalesTable = ({ data, prepareUpdate, isFetching = false }) => {
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [tableData, setTableData] = useState([]);

  // Update tableData when parent data changes
  React.useEffect(() => {
    const normalizedData = (Array.isArray(data) ? data : []).map((item, idx) => ({
      ...item,
      sales_person_id: item?.sales_person_id || `row-${idx}`,
      first_name: item?.first_name || '',
      last_name: item?.last_name || '',
      job_title: item?.job_title || '',
      mobile: item?.mobile || item?.mobileNum || '',
      email: item?.email || '',
      manager_name: item?.manager_name || '',
      user_name: item?.user_name || '',
      status: item?.status || 'inactive',
    }));
    setTableData(normalizedData);
    // Reset filtered data and clear filters when source data changes
    setFilteredData(normalizedData);
    setAppliedFilters([]);
  }, [data]);

  // Apply filters to the data
  React.useEffect(() => {
    if (!tableData.length) return;

    console.log('Applied Filters:', appliedFilters);

    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      const filtered = tableData.filter((item) => {
        // Check if item matches any of the filter conditions
        return appliedFilters.some(({ category, items }) => {
          if (!items || items.length === 0) return false;

          switch (category) {
            case 'Name':
              const fullName = `${item.first_name} ${item.last_name}`.trim().toLowerCase();
              return items.some((filterName) => filterName.toLowerCase() === fullName);
            case 'Mobile':
              const itemMobile = (item.mobile || '').toString();
              return items.some((filterMobile) => filterMobile === itemMobile);
            case 'Email':
              return items.some((filterEmail) => filterEmail === item.email);
            case 'Title':
              return items.some((filterTitle) => filterTitle === item.job_title);
            case 'Reports to':
              return items.some((filterManager) => filterManager === item.manager_name);
            case 'User Name':
              return items.some((filterUserName) => filterUserName === item.user_name);
            case 'Status':
              return items.some((filterStatus) => filterStatus === item.status);
            default:
              return false;
          }
        });
      });

      console.log('Filtered Data:', filtered);
      setFilteredData(filtered);
    } else {
      console.log('No filters applied, showing all data');
      setFilteredData(tableData);
    }
  }, [appliedFilters, tableData]);

  // Use filtered data for display
  const displayData =
    Array.isArray(appliedFilters) && appliedFilters.length > 0 ? filteredData : tableData;

  const columns = [
    {
      name: 'sales_person_id',
      label: 'Sales Person ID',
      options: {
        display: false,
        filter: false,
        filterType: 'none',
        filterList: [],
      },
    },
    {
      name: 'first_name',
      label: 'Name',
      options: {
        customBodyRender: (value, tableMeta) => {
          const handleClick = (e) => {
            e.stopPropagation();
            const sales_person_id = tableMeta.rowData[0];
            const row = displayData[tableMeta.rowIndex];
            // Pass the current displayed row data for comparison
            prepareUpdate(sales_person_id, {
              sales_person_id: row.sales_person_id,
              first_name: row.first_name || '',
              last_name: row.last_name || '',
              job_title: row.job_title || '',
              mobile: row.mobile || '',
              email: row.email || '',
              manager_name: row.manager_name || '',
              status: row.status || 'inactive',
            });
          };
          // Combine first and last name only
          const row = displayData[tableMeta.rowIndex];
          const fullName = `${row.first_name} ${row.last_name}`.trim();
          // Ellipsis if too long
          return (
            <h3
              className="text-base font-bold text-auxiliary-800 cursor-pointer hover:text-secondary-700 truncate max-w-[180px] md:max-w-[240px] lg:max-w-[320px]"
              onClick={handleClick}
              title={fullName}
            >
              {fullName || '-'}
            </h3>
          );
        },
      },
    },
    {
      name: 'job_title',
      label: 'Title',
      options: {
        customBodyRender: (value, tableMeta) => {
          if (tableData[tableMeta.rowIndex]?.first_name === 'FETCHING_ROW') return null;
          return value || '-';
        },
      },
    },
    {
      name: 'mobile',
      label: 'Mobile',
      options: {
        customBodyRender: (value, tableMeta) => {
          if (tableData[tableMeta.rowIndex]?.first_name === 'FETCHING_ROW') return null;
          if (!value) return '-';
          return formatPhone(value) || '-';
        },
      },
    },
    {
      name: 'email',
      label: 'Email',
      options: {
        customBodyRender: (value, tableMeta) => {
          if (tableData[tableMeta.rowIndex]?.first_name === 'FETCHING_ROW') return null;
          return value || '-';
        },
      },
    },
    {
      name: 'manager_name',
      label: 'Reports to',
      options: {
        customBodyRender: (value, tableMeta) => {
          if (tableData[tableMeta.rowIndex]?.first_name === 'FETCHING_ROW') return null;
          return value || '-';
        },
      },
    },
    {
      name: 'user_name',
      label: 'User Name',
      options: {
        customBodyRender: (value, tableMeta) => {
          if (tableData[tableMeta.rowIndex]?.first_name === 'FETCHING_ROW') return null;
          return value || '-';
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: (value, tableMeta) => {
          if (tableData[tableMeta.rowIndex]?.first_name === 'FETCHING_ROW') return null;
          return (
            <p
              className={`py-2 rounded-full w-32 text-left ${
                value === 'active'
                  ? 'border-secondary-500 text-secondary-500'
                  : 'border-danger-500 text-danger-500'
              }`}
            >
              {value || 'inactive'}
            </p>
          );
        },
      },
    },
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 20, 50, 100, 200],
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    onRowClick: (_, rowMeta) => {
      const currentRow = displayData[rowMeta.rowIndex];
      if (currentRow && currentRow.sales_person_id) {
        // Pass the current displayed row data for comparison
        prepareUpdate(currentRow.sales_person_id, {
          sales_person_id: currentRow.sales_person_id,
          first_name: currentRow.first_name || '',
          last_name: currentRow.last_name || '',
          job_title: currentRow.job_title || '',
          mobile: currentRow.mobile || '',
          email: currentRow.email || '',
          manager_name: currentRow.manager_name || '',
          status: currentRow.status || 'inactive',
        });
      }
    },
    customToolbarSelect: () => {},
    textLabels: {
      body: {
        noMatch: isFetching ? '' : 'No matching records found',
      },
    },
    setTableProps: () => ({
      style: { cursor: 'pointer' },
    }),
  };

  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleClosePopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    // Apply the selected filters with category information
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  return (
    <div className="w-full min-h-[400px] flex flex-col justify-start relative overflow-x-auto md:overflow-x-visible p-0 m-0">
      <div className="w-full min-w-max">
        <MyTable
          title=""
          data={displayData}
          columns={columns}
          options={options}
          onFilterClick={handleFilterClick}
        />
      </div>
      {isFetching && (
        <div
          className="absolute left-0 right-0 z-10 pointer-events-none"
          style={{
            top: '130px',
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255,255,255,0.85)',
          }}
        >
          <span className="text-lg font-semibold text-secondary-700">Fetching...</span>
        </div>
      )}
      {isFilterPopupOpen && (
        <FilterPopup
          onClose={handleClosePopup}
          onApply={handleApplyFilters}
          tableData={tableData}
          categories={['Name', 'Title', 'Mobile', 'Email', 'Reports to', 'User Name', 'Status']}
          getSearchResults={(searchTerm, selectedCategory) => {
            if (!searchTerm || !selectedCategory || !tableData) return [];

            return tableData
              .map((item) => {
                switch (selectedCategory) {
                  case 'Name':
                    return `${item.first_name} ${item.last_name}`.trim();
                  case 'Title':
                    return item.job_title;
                  case 'Mobile':
                    return item.mobile;
                  case 'Email':
                    return item.email;
                  case 'Reports to':
                    return item.manager_name;
                  case 'User Name':
                    return item.user_name;
                  case 'Status':
                    return item.status;
                  default:
                    return null;
                }
              })
              .filter((value) => value && value.toLowerCase().includes(searchTerm.toLowerCase()));
          }}
        />
      )}
    </div>
  );
};

export default SalesTable;
